import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import useGetDeviceType from "../../../../hooks/useGetDeviceType"
import { TStore } from "../../../../store/Store"
import { setInternalSitesFilters, setPage, setSitesFilters, SitesFilters, SitesInternalFilters } from "../../SiteSlice"
import { useModal } from "../../../../hooks/useModal"
import { useLazyGetAllSitesQuery } from "../../SiteService"

const useSitesFilter = ({ tableStates, tableFunctions }: any) => {
    const dispatch = useDispatch()
    const { numOfRows, sortAddedOnCount, page, sortAddedOn } = tableStates
    const [openAddForm, setOpenAddForm] = useState(false)
    const [isActive, setActive] = useState(false)

    const { isShown, toggle } = useModal()
    const { filterValues, internalFilterValues } = useSelector((store: TStore) => store.sites)
    const { deviceType } = useGetDeviceType()
    const [getAllSites, { data: sitesData, isLoading: isSitesLoading }] = useLazyGetAllSitesQuery()

    const handleDownload = async () => {
        const { siteType, state, city, status, addedBy, tags, country_id, ...filters } = filterValues
        const stateFilter = JSON.stringify(state?.length ? state.map(i => i.id) : [])
        const cityFilter = JSON.stringify(city?.length ? city.map(i => i.id) : [])
        const statusFilter = status?.id === 0 || status?.id === 1 ? status.id : ""
        const siteTypeFilter = siteType?.title?.toLowerCase() || ""
        const addedByFilter = JSON.stringify(addedBy?.length ? addedBy.map(i => i.id) : [])
        const tagsFilter = JSON.stringify(tags?.length ? tags.map(i => i.id) : [])
        const siteCountryFilter = country_id?.id

        const sortParams: Record<string, boolean> = sortAddedOn
            ? { created_at: true }
            : sortAddedOnCount > 0
              ? { created_at: false }
              : { updated_at: true }
        const res: any = await getAllSites({
            page,
            per_page: numOfRows,
            state: stateFilter,
            city: cityFilter,
            site_type: siteTypeFilter,
            status: statusFilter,
            added_by: addedByFilter,
            tag_ids: tagsFilter,
            country_id: siteCountryFilter,
            ...sortParams,
            ...filters,
            showOnlyActive: false,
            download: true,
        }).unwrap()
        const downloadUrl = res?.link?.urls.download as string
        window.open(downloadUrl, "_blank")
    }

    const toggleAccordion = () => {
        setActive(value => !value)
    }

    const resetInternalFilters = (fullReset: boolean = false) => {
        dispatch(
            setInternalSitesFilters({
                state: [],
                city: [],
                status: {},
                siteType: {},
                country_id: {},
                addedBy: [],
                tags: [],
            })
        )
        dispatch(
            fullReset
                ? setSitesFilters({
                      site: "",
                      pincode: "",
                      state: [],
                      city: [],
                      status: {},
                      siteType: {},
                      country_id: {},
                      addedBy: [],
                      tags: [],
                  })
                : setSitesFilters({
                      ...filterValues,
                      state: [],
                      city: [],
                      status: {},
                      siteType: {},
                      country_id: {},
                      addedBy: [],
                      tags: [],
                  })
        )
    }

    const resetFilters = () => {
        dispatch(
            setSitesFilters({
                site: "",
                pincode: "",
            })
        )
        resetInternalFilters(true)
        dispatch(setPage(1))
    }

    /**
     * This function will trigger the api call when the values of filter are changed using this.
     * @param key - key names of the filters in the slice
     * @param value
     * @param isInternal (Optional) - to be true for filters in the side drawer
     */
    const handleFilterChange = (
        key: keyof SitesFilters | keyof SitesInternalFilters,
        value: string | number | [{ id: number; title: string }],
        isInternal: boolean = false
    ) => {
        if (isInternal) {
            dispatch(
                setInternalSitesFilters({
                    ...internalFilterValues,
                    [key]: value,
                })
            )
        } else
            dispatch(
                setSitesFilters({
                    ...filterValues,
                    [key]: value,
                })
            )
        dispatch(setPage(1))
    }

    const applyInternalFilters = () => {
        toggle()
        dispatch(
            setSitesFilters({
                ...filterValues,
                ...internalFilterValues,
            })
        )
        dispatch(setPage(1))
    }
    const closeFilter = () => {
        return toggle()
    }

    const states = {
        filterValues,
        deviceType,
        openAddForm,
        isShown,
        isActive,
        internalFilterValues,
        isSitesLoading,
    }
    const functions = {
        resetFilters,
        handleFilterChange,
        setOpenAddForm,
        toggle,
        closeFilter,
        setActive,
        toggleAccordion,
        resetInternalFilters,
        applyInternalFilters,
        handleDownload,
    }

    return {
        states,
        functions,
    }
}

export type SitesFilterStates = ReturnType<typeof useSitesFilter>["states"]
export type SitesFilterFunctions = ReturnType<typeof useSitesFilter>["functions"]

export default useSitesFilter
